<template>
    <form @submit.prevent="confirm">
        <FormGroup>
            <Users
                :disabled="loading"
                :hasError="hasError"
                :searchFunction="fetchUsers"
                placeholder="Enter a user id"
                ref="users"
                v-model="targetId"
                @remove="remove"
            />
            <Error v-if="hasError" :error="targetErrors" />
            <InputHint>Target user to be merged into</InputHint>
        </FormGroup>

        <Table v-show="target" striped borderless>
            <template v-slot:thead>
                <tr>
                    <th></th>
                    <th>Current user</th>
                    <th></th>
                    <th>Target user</th>
                </tr>
            </template>
            <tr name="ID">
                <td>ID:</td>
                <td class="text-decoration-line-through text-danger">{{ user.id }}</td>
                <td><Icon icon="arrow-right" /></td>
                <td class="text-success">{{ target && target.id }}</td>
            </tr>
            <tr>
                <td>Name:</td>
                <td class="text-decoration-line-through text-danger">{{ user.name }}</td>
                <td><Icon icon="arrow-right" /></td>
                <td class="text-success">{{ target && target.name }}</td>
            </tr>
            <tr>
                <td>E-mail</td>
                <td class="text-decoration-line-through text-danger">{{ user.email }}</td>
                <td><Icon icon="arrow-right" /></td>
                <td class="text-success">{{ target && target.email }}</td>
            </tr>
        </Table>
        <Button type="submit" icon="check" :disabled="loading || !isSubmittable" :loading="loading">Merge</Button>
    </form>
</template>

<script>
    import { Alerts, Button, Error, FormGroup, Icon, InputHint, Table } from '@tech_hexeko/design-system'
    import Users from '@tech_hexeko/design-system/src/components/Form/Models/Users'
    import Swal from 'sweetalert2'

    export default {
        components: {
            Button,
            Error,
            FormGroup,
            Icon,
            InputHint,
            Table,
            Users,
        },
        props: {
            user: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                targetId: '',
                target: null,
                targetErrors: [],
                loading: false,
            }
        },
        computed: {
            isSubmittable() {
                return this.user.id && this.targetId && !this.isSameUser(this.targetId)
            },
            hasError() {
                return this.targetErrors.length > 0
            },
        },
        methods: {
            fetchUsers(search) {
                return this.$api.users.get({
                    search: search,
                })
            },
            async submit() {
                if (!this.isSubmittable) return
                this.targetErrors = []
                const data = {
                    to: this.targetId,
                }
                try {
                    const response = await this.$api.users.merge(this.user.id, data)
                    const successMessage = response?.message || 'Successfully merged'
                    this.$emit('submitted', successMessage)
                } catch (axiosError) {
                    const errorMessage = this.getErrorMessage(axiosError)
                    this.targetErrors = axiosError.response?.data?.errors?.to
                    Alerts.notificationError(errorMessage)
                }
            },

            async confirm() {
                this.loading = true
                try {
                    const result = await Swal.fire({
                        title: `You will delete user ${this.user.id} and keep ${this.targetId}.`,
                        text: `This action is irreversible`,
                        icon: 'warning',
                        showCancelButton: true,
                        buttonsStyling: false,
                        customClass: {
                            confirmButton: 'btn btn-success text-white btn-lg me-2',
                            cancelButton: 'btn btn-light text-dark btn-lg',
                        },
                        confirmButtonText: this.$t('confirm.yes'),
                        cancelButtonText: this.$t('confirm.no'),
                    })
                    if (!result.value) return
                    await this.submit()
                } finally {
                    this.loading = false
                }
            },

            getErrorMessage(axiosError) {
                const error = axiosError.response.data

                if (error?.errors && Object.keys(error.errors).length > 0) {
                    return '\nErrors:\n- ' + Object.values(error.errors).flat().join('\n- ')
                }
                return error?.message || 'An error has occurred'
            },
            isSameUser(id) {
                return this.user.id === id
            },
            // When de-selecting, Users component doesn't update v-model with empty value, so we have to manage it ourselves
            remove() {
                this.targetId = ''
            },
        },
        watch: {
            // having to manually sync the user object with the user ID
            // - we have to manually get the target user from the inner multiselect because components don't expose it
            // - we also have to manually sync the value of target everytime targetId changes because refs are not reactive
            targetId(targetId) {
                this.targetErrors = []
                this.$nextTick(() => {
                    if (this.isSameUser(targetId)) {
                        this.targetErrors = ['You cannot merge into the same user']
                    }
                    this.target = this.$refs.users.$refs.usersSelect.$refs.multiselect.getValue()
                })
            },
        },
    }
</script>
